<template>
  <!--
    ArtifactDetail SECTION — Patient Flag artifact, "Presentation" tab.

    WHAT: Presentation config for how a flag renders — key, color, priority,
      active, boolean-vs-categorical type (with a per-value label/color/chip-
      priority/weight map), and an optional hover VALUE display (label/format/
      suffix). Structured editor lifted from FlagDetail v1.

    CONTRACT (props { record, artifact, context }):
      READS  record.{key, color, priority, is_active} and record.flag_config
             (flag_config.type, flag_config.values[], flag_config.value_display).
      EMITS  patch({ key, color, priority, is_active, flag_config }) — flag_config
             rebuilt by buildFlagConfig() ({} for boolean, or {type:'categorical',
             values:[...]}, plus value_display when the hover toggle is on).
      ALSO calls its own endpoint: the "Refresh state" button POSTs
             /api/impact_analytics/patient-flags/{id}/refresh/ to recompute
             PATIENT_FLAG_STATE as-of-now and shows the returned on_count. (This
             refresh does NOT emit `patch`/`refresh` — it is a direct action.)

    NOTES:
      - hydrating guard: set false on $nextTick after mount; emit() returns early
        while hydrating (fields saved on @input/@change).
      - NOT selfChrome — rendered inside ArtifactDetail's standard titled card.
  -->
  <div>
    <div class="flex items-center justify-end mb-3">
      <button
        class="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-gray-800 rounded-lg px-3 py-1.5 hover:bg-gray-700 disabled:opacity-50"
        :disabled="refreshing"
        @click="doRefresh"
      >{{ refreshing ? 'Refreshing…' : 'Refresh state' }}</button>
      <span v-if="onCount !== null" class="text-xs text-gray-600 ml-3">
        <span class="font-semibold text-gray-900">{{ onCount }}</span> patients ON
      </span>
    </div>

    <div class="grid grid-cols-5 gap-3 mb-4">
      <div class="col-span-2">
        <label class="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Key</label>
        <input v-model="key" placeholder="long_stay" class="w-full text-xs font-mono px-2 py-1.5 border border-gray-200 rounded-lg" @input="emit" />
      </div>
      <div>
        <label class="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Color</label>
        <select v-model="color" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emit">
          <option v-for="c in colors" :key="c" :value="c">{{ c }}</option>
        </select>
      </div>
      <div>
        <label class="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Priority</label>
        <input v-model.number="priority" type="number" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @input="emit" />
      </div>
      <div class="flex items-end">
        <label class="flex items-center gap-2 text-xs text-gray-600">
          <input v-model="isActive" type="checkbox" class="rounded" @change="emit" /> Active
        </label>
      </div>
    </div>

    <!-- type -->
    <div class="border-t border-gray-100 pt-3 mb-3">
      <div class="flex items-center gap-4 mb-2">
        <label class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Type</label>
        <label class="flex items-center gap-1.5 text-xs text-gray-700">
          <input v-model="flagType" type="radio" value="boolean" @change="onType" /> Boolean
        </label>
        <label class="flex items-center gap-1.5 text-xs text-gray-700">
          <input v-model="flagType" type="radio" value="categorical" @change="onType" /> Categorical
        </label>
      </div>

      <div v-if="flagType === 'categorical'">
        <div class="grid grid-cols-12 gap-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400 px-1 mb-1">
          <div class="col-span-2">Value</div><div class="col-span-4">Label</div>
          <div class="col-span-2">Color</div><div class="col-span-2">Chip priority</div>
          <div class="col-span-1">Weight</div><div class="col-span-1"></div>
        </div>
        <div v-for="(v, i) in categoricalValues" :key="i" class="grid grid-cols-12 gap-2 mb-1.5 items-center">
          <input v-model.number="v.value" type="number" class="col-span-2 text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @input="emit" />
          <input v-model="v.label" class="col-span-4 text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @input="emit" />
          <select v-model="v.color" class="col-span-2 text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emit">
            <option v-for="c in colors" :key="c" :value="c">{{ c }}</option>
          </select>
          <input v-model.number="v.priority" type="number" class="col-span-2 text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @input="emit" />
          <input v-model.number="v.weight" type="number" class="col-span-1 text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @input="emit" />
          <button class="col-span-1 text-xs text-red-500 hover:text-red-700" @click="removeValue(i)">✕</button>
        </div>
        <button class="mt-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1 hover:bg-gray-50" @click="addValue">+ Add value</button>
      </div>
    </div>

    <!-- hover value display -->
    <div class="border-t border-gray-100 pt-3">
      <label class="flex items-center gap-2 text-xs text-gray-700 mb-2">
        <input v-model="useValueDisplay" type="checkbox" class="rounded" @change="emit" />
        Show the flag's <span class="font-mono">VALUE</span> on hover
      </label>
      <div v-if="useValueDisplay" class="grid grid-cols-12 gap-2 items-center">
        <div class="col-span-5">
          <label class="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Hover label</label>
          <input v-model="valueDisplay.label" placeholder="LACE score" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @input="emit" />
        </div>
        <div class="col-span-4">
          <label class="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Format</label>
          <select v-model="valueDisplay.format" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emit">
            <option value="integer">Integer (14)</option>
            <option value="percent">Percent (0.42 → 42%)</option>
            <option value="decimal">Decimal (0.43)</option>
            <option value="raw">Raw</option>
          </select>
        </div>
        <div class="col-span-3">
          <label class="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Suffix</label>
          <input v-model="valueDisplay.suffix" placeholder=" days" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @input="emit" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FlagPresentationSection',
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    const fc = this.record.flag_config || {};
    const isCat = fc.type === 'categorical';
    const vd = fc.value_display;
    return {
      colors: ['gray', 'red', 'amber', 'sky', 'emerald', 'violet'],
      key: this.record.key || '',
      color: this.record.color || 'gray',
      priority: this.record.priority ?? 99,
      isActive: this.record.is_active !== false,
      flagType: isCat ? 'categorical' : 'boolean',
      categoricalValues: isCat ? (fc.values || []).map((v) => ({
        value: v.value, label: v.label || '', color: v.color || 'gray',
        priority: v.priority ?? 1, weight: v.weight ?? 0,
      })) : [],
      useValueDisplay: !!(vd && typeof vd === 'object'),
      valueDisplay: { label: (vd && vd.label) || '', format: (vd && vd.format) || 'integer', suffix: (vd && vd.suffix) || '' },
      refreshing: false,
      onCount: null,
      hydrating: true,
    };
  },
  mounted() { this.$nextTick(() => { this.hydrating = false; }); },
  methods: {
    onType() {
      if (this.flagType === 'categorical' && !this.categoricalValues.length) this.addValue();
      this.emit();
    },
    addValue() {
      this.categoricalValues.push({ value: this.categoricalValues.length + 1, label: '', color: 'gray', priority: 1, weight: 0 });
    },
    removeValue(i) { this.categoricalValues.splice(i, 1); this.emit(); },
    buildFlagConfig() {
      const fc = this.flagType === 'categorical'
        ? { type: 'categorical', values: this.categoricalValues.filter((v) => v.value != null && v.value !== '')
            .map((v) => ({ value: v.value, label: v.label, color: v.color, priority: v.priority, weight: v.weight })) }
        : {};
      if (this.useValueDisplay) {
        const d = { format: this.valueDisplay.format || 'integer' };
        if (this.valueDisplay.label) d.label = this.valueDisplay.label;
        if (this.valueDisplay.suffix) d.suffix = this.valueDisplay.suffix;
        fc.value_display = d;
      }
      return fc;
    },
    emit() {
      if (this.hydrating) return;
      this.$emit('patch', {
        key: this.key,
        color: this.color,
        priority: this.priority,
        is_active: this.isActive,
        flag_config: this.buildFlagConfig(),
      });
    },
    async doRefresh() {
      this.refreshing = true;
      try {
        const { data } = await axios.post(`api/impact_analytics/patient-flags/${this.record.id}/refresh/`, {});
        this.onCount = data.on_count;
      } catch (e) {
        console.error('Failed to refresh flag state:', e?.response?.data || e.message);
      } finally {
        this.refreshing = false;
      }
    },
  },
};
</script>
